import { useState, useEffect } from 'react';
import supabase from '../supabase';
import RetroVideoPlayer from './RetroVideoPlayer';

function VideoTeaser() {
    const [teaser, setTeaser] = useState(null);
    const [videos, setVideos] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
    const [hasMounted, setHasMounted] = useState(false);
    const [revealed, setRevealed] = useState(false);
    const [activeVideo, setActiveVideo] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 600);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchAll = async () => {
            const { data: teaserData } = await supabase.from('video_teaser').select('*').eq('enabled', true).maybeSingle();
            if (!teaserData) return;
            setTeaser(teaserData);

            const { data: campaignData } = await supabase.from('campaigns').select('year').eq('is_active', true).maybeSingle();
            if (!campaignData) return;

            const { data: videosData } = await supabase
                .from('campaign_videos')
                .select('*')
                .eq('campaign_year', campaignData.year)
                .order('order_index', { ascending: true });
            if (videosData) setVideos(videosData);
        };
        fetchAll();
    }, []);

    useEffect(() => {
        const t = requestAnimationFrame(() => setHasMounted(true));
        return () => cancelAnimationFrame(t);
    }, []);

    if (!teaser || !teaser.image_url || videos.length === 0) return null;

    const size = isMobile ? 100 : 180;
    const hiddenAmount = size * 0.7;
    const restingOffset = isMobile ? 72 : 56;
    const edgeOffset = isMobile ? 12 : 24;

    const position = teaser.position || 'bottom-left';
    const vertical = position.startsWith('top') ? 'top' : position.startsWith('middle') ? 'middle' : 'bottom';
    const isRightSide = position.endsWith('right');
    const is1_3 = position.endsWith('1-3');
    const is2_3 = position.endsWith('2-3');

    // Détermine l'axe de mouvement : "middle" glisse horizontalement (le dessin
    // vit sur le bord gauche/droite de l'écran), top/bottom glissent verticalement.
    const moveAxis = vertical === 'middle' ? 'horizontal' : 'vertical';

    // Position du conteneur fixe (zone de hover stable)
    let containerStyle = {};
    if (vertical === 'top') containerStyle.top = 0;
    else if (vertical === 'bottom') containerStyle.bottom = 0;
    else containerStyle.top = '50%'; // middle

    if (moveAxis === 'horizontal') {
        // Le conteneur colle au bord gauche ou droit de l'écran
        if (isRightSide) containerStyle.right = 0;
        else containerStyle.left = 0;
    } else {
        // top/bottom : position horizontale fixe selon left/right/1-3/2-3
        if (isRightSide) containerStyle.right = `${edgeOffset}px`;
        else if (is1_3) { containerStyle.left = '33.33%'; containerStyle.transform = 'translateX(-50%)'; }
        else if (is2_3) { containerStyle.left = '66.66%'; containerStyle.transform = 'translateX(-50%)'; }
        else containerStyle.left = `${edgeOffset}px`;
    }

    const containerWidth = moveAxis === 'horizontal' ? restingOffset + size : size;
    const containerHeight = moveAxis === 'horizontal' ? size : restingOffset + size;

    const dessinStyle = moveAxis === 'horizontal'
        ? {
            width: `${size}px`, height: `${size}px`, cursor: 'pointer',
            [isRightSide ? 'marginRight' : 'marginLeft']: `${revealed ? restingOffset : restingOffset - hiddenAmount}px`,
            transition: hasMounted ? `${isRightSide ? 'margin-right' : 'margin-left'} 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)` : 'none',
        }
        : {
            width: `${size}px`, height: `${size}px`, cursor: 'pointer',
            [vertical === 'top' ? 'marginTop' : 'marginBottom']: `${revealed ? restingOffset : restingOffset - hiddenAmount}px`,
            transition: hasMounted ? `${vertical === 'top' ? 'margin-top' : 'margin-bottom'} 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)` : 'none',
        };

    return (
        <>
            <div
                onMouseEnter={() => setRevealed(true)}
                onMouseLeave={() => setRevealed(false)}
                onTouchStart={() => setRevealed(true)}
                onTouchEnd={() => setTimeout(() => setRevealed(false), 300)}
                style={{
                    position: 'fixed',
                    ...containerStyle,
                    ...(vertical === 'middle' && moveAxis === 'horizontal' ? { transform: 'translateY(-50%)' } : {}),
                    width: `${containerWidth}px`,
                    height: `${containerHeight}px`,
                    zIndex: 40,
                    display: 'flex',
                    alignItems: moveAxis === 'horizontal' ? 'center' : (vertical === 'top' ? 'flex-start' : 'flex-end'),
                    justifyContent: moveAxis === 'horizontal' ? (isRightSide ? 'flex-end' : 'flex-start') : 'center',
                }}
            >
                <div onClick={() => setActiveVideo(videos[0])} style={dessinStyle}>
                    <img src={teaser.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                </div>
            </div>

            {activeVideo && (
                <div
                    onClick={() => setActiveVideo(null)}
                    style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(27,27,24,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            width: 'min(400px, 100%)',
                            background: '#f4f1ec',
                            border: '2px solid #fff',
                            borderRight: '2px solid #8a8680',
                            borderBottom: '2px solid #8a8680',
                            boxShadow: '6px 6px 0 rgba(0,0,0,0.4)',
                            fontFamily: "'MS Sans Serif', 'Tahoma', Arial, sans-serif",
                        }}
                    >
                        <div style={{
                            background: 'linear-gradient(90deg, #1b1b18, #3a3632)',
                            color: '#f4f1ec', padding: '5px 8px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            fontSize: '13px', fontWeight: 'bold',
                        }}>
                            <span>{activeVideo.title || `campaign_${activeVideo.campaign_year}.mp4`}</span>
                            <div
                                onClick={() => setActiveVideo(null)}
                                style={{ width: '16px', height: '14px', background: '#f4f1ec', border: '1px solid #fff', borderRight: '1px solid #8a8680', borderBottom: '1px solid #8a8680', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#1b1b18', cursor: 'pointer' }}
                            >
                                ✕
                            </div>
                        </div>

                        <RetroVideoPlayer src={activeVideo.video_url} />
                    </div>
                </div>
            )}
        </>
    );
}

export default VideoTeaser;